import { useCallback, useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation';
import {
	type AgentKnowledgeConfiguration,
	type AssistanceServiceInjectedParams,
	createHookWithAssistanceService,
	useQuery,
} from '@atlassian/conversation-assistant-service';

/** Do not import for non Atlassian studio */
export const useKnowledgeConnectors = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		enabled = true,
	}: {
		enabled?: boolean;
	} & AssistanceServiceInjectedParams) => {
		const connectorsQuery = useQuery({
			queryKey: ['getKnowledgeConnectors'],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
				});

				return assistanceService.getAgentKnowledgeConfiguration();
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
					error,
				});
			},
			enabled,
		});

		const isConnectorEnabled = useCallback(
			(type: AgentKnowledgeConfiguration['source']): boolean => {
				return (
					connectorsQuery.data?.knowledgeSources?.some((value) => value.source === type) ?? false
				);
			},
			[connectorsQuery.data],
		);

		const enabledKnowledgeSources = useMemo((): AgentKnowledgeConfiguration[] => {
			return connectorsQuery.data?.knowledgeSources ?? [];
		}, [connectorsQuery.data]);

		return {
			enabledKnowledgeSources,
			isConnectorEnabled,
		};
	},
);
